import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Button, InputField } from '../styles/CommonElements';
import BotPrevention from './BotPrevention';
import { trackFormOpen, trackFormSubmit } from '../ga-helpers';

const NewsletterSection = styled.section`
  background-color: transparent;
  padding: 4rem 0;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 3rem 0;
  }
  
  @media (max-width: 480px) {
    padding: 2.5rem 0;
  }
`;

const NewsletterTitle = styled.h3`
  font-size: 1.75rem;
  margin-bottom: 1rem;
`;

const NewsletterDescription = styled.p`
  color: #666;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const SubscribeFormContainer = styled.div`
  max-width: 500px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SubscribeForm = styled.form`
  display: flex;
  width: 100%;
  
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const InputContainer = styled.div`
  flex: 1;
  margin-right: 1rem;
  
  @media (max-width: 600px) {
    margin-right: 0;
  }
`;

const StatusMessage = styled.div<{ isError: boolean }>`
  width: 100%;
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 6px;
  background-color: ${props => props.isError ? '#ffebee' : '#e8f5e9'};
  color: ${props => props.isError ? '#c62828' : '#2e7d32'};
  font-weight: 500;
`;

const HoneypotInput = styled.input`
  display: none;
`;

// Supabase project constants
const SUPABASE_PROJECT_ID = 'skxqpztdcgnmncooiopw';
const FUNCTION_URL = `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/contact-form`;

const Newsletter: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isHumanVerified, setIsHumanVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ message: string, isError: boolean } | null>(null);
  const [honeypot, setHoneypot] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  // Track newsletter form view when component mounts
  useEffect(() => {
    trackFormOpen('Newsletter Form');
  }, []);

  const handleValidationChange = (isValid: boolean) => {
    setIsHumanVerified(isValid);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check - if this field is filled, it's probably a bot
    if (honeypot) {
      console.log('Bot detected via honeypot');
      // Fake success to confuse bots
      setStatusMessage({
        message: 'Thank you for subscribing to our newsletter!',
        isError: false
      });
      return;
    }

    // Verify human validation
    if (!isHumanVerified) {
      console.log('Human verification failed');
      setStatusMessage({
        message: 'Something went wrong. Please try again.',
        isError: true
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatusMessage({
        message: 'Please enter a valid email address.',
        isError: true
      });
      return;
    }

    try {
      setIsSubmitting(true);
      setStatusMessage(null);

      // 1. Insert into newsletter table
      const insertResponse = await fetch(`https://${SUPABASE_PROJECT_ID}.supabase.co/rest/v1/newsletters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({
          email,
          user_agent: navigator.userAgent,
          source: 'website_newsletter'
        })
      });

      if (!insertResponse.ok) {
        // If it's a duplicate email, don't show an error
        if (insertResponse.status === 409) {
          console.log('Email already subscribed');
        } else {
          throw new Error('Failed to insert subscription');
        }
      }

      // 2. Send confirmation emails using the contact form function
      const notificationData = {
        full_name: 'Newsletter Subscriber',
        email: email,
        message: `New newsletter subscription from the website: ${email}`,
        service_type: 'Newsletter Subscription',
        is_newsletter: true // Specific flag for newsletter to handle differently in the function
      };

      const emailResponse = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificationData)
      });

      if (!emailResponse.ok) {
        console.error('Email notification error:', await emailResponse.text());
      }

      // Track newsletter signup success in GA
      trackFormSubmit('Newsletter Form', {
        source: 'website_newsletter'
      });

      // Show success message
      setStatusMessage({
        message: 'Thank you for subscribing to our newsletter! We have sent a confirmation email to your inbox.',
        isError: false
      });

      // Reset the form
      setEmail('');
      if (formRef.current) {
        formRef.current.reset();
      }

    } catch (error) {
      console.error('Subscription error:', error);
      setStatusMessage({
        message: 'Something went wrong. Please try again later.',
        isError: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <NewsletterSection id="newsletter">
      <div className="container font--jakarta">
        <NewsletterTitle>Stay up to date with our news, ideas and updates</NewsletterTitle>
        <NewsletterDescription>
          Subscribe to our newsletter to receive the latest updates, industry insights, and exclusive offers from Whitby's premier software development company.
        </NewsletterDescription>

        <SubscribeFormContainer>
          <SubscribeForm onSubmit={handleSubmit} ref={formRef}>
            <InputContainer>
              <InputField
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </InputContainer>

            {/* Invisible bot prevention */}
            <BotPrevention onValidationChange={handleValidationChange} formId="newsletter" />

            {/* Hidden honeypot field */}
            <HoneypotInput
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              autoComplete="off"
            />

            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'SUBSCRIBING...' : 'SUBSCRIBE NOW'}
            </Button>
          </SubscribeForm>

          {statusMessage && (
            <StatusMessage isError={statusMessage.isError}>
              {statusMessage.message}
            </StatusMessage>
          )}
        </SubscribeFormContainer>
      </div>
    </NewsletterSection>
  );
};

export default Newsletter; 